import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Modal de creacion/edicion de cobro a cliente con solicitud de factura.
// Extraido de CobrosCliente.js (Fase A, #4) para poder reutilizarse tambien
// desde la matriz de Proyeccion. Comportamiento identico al original.
//
// Props:
//   editando      bool   - true = editar cobro existente (initialForm.id requerido)
//   initialForm   obj    - estado inicial del formulario (cliente_id, hitos[], etc.)
//   clientes      array  - lista de clientes para el selector
//   proyectos     array  - lista de proyectos para el selector
//   onClose       fn()   - cerrar sin guardar
//   onSaved       fn(cobroId) - tras guardar (o guardar+solicitar) con exito

import { API } from '../config/api';
import PreviewCorreo from './PreviewCorreo';
const ESTADOS_COBRO = ['Pendiente', 'Parcial', 'Facturada', 'Cobrada', 'Anulado'];
const MONEDAS       = ['Colones', 'Dólares'];
const EMPRESAS_FACT = ['JyA Conusa', 'Cuenta Personal (JCNS)'];

const inputStyle = {
  padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '8px',
  fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box',
  fontFamily: 'Segoe UI, sans-serif'
};

const fmt = (n, moneda) => {
  const sym = moneda === 'Dólares' ? '$' : '₡';
  return `${sym}${Number(n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
};

const montoConIva = (h) => {
  const base = parseFloat(h.monto) || 0;
  const iva  = parseFloat(h.iva_porcentaje ?? 13) || 0;
  return base * (1 + iva / 100);
};

export function ModalCobroCliente({ editando = false, initialForm, clientes = [], proyectos = [], onClose, onSaved }) {
  const [form, setForm]                               = useState(initialForm || null);
  const [mostrarCobrados, setMostrarCobrados]         = useState(false); // v42: colapsar hitos ya cobrados
  const [ocActiva, setOcActiva]                       = useState(null);  // v43: OC activa del proyecto (warning de fondos)
  const [ocCargada, setOcCargada]                     = useState(false); // v43: la consulta de OC ya termino
  const [ocSkip, setOcSkip]                           = useState(false); // v43: el usuario eligio "Sin OC (no aplica)"
  const [ocForm, setOcForm]                           = useState({ monto_sin_iva: '', numero_oc: '', fecha_emision: '', moneda: '' });
  const [ocGuardando, setOcGuardando]                 = useState(false);
  const [error, setError]                             = useState('');
  const [guardando, setGuardando]                     = useState(false);
  const [previewSolicitud, setPreviewSolicitud]       = useState(null);
  const [lineasOferta, setLineasOferta]               = useState([]);
  const [hitosOfertaCargando, setHitosOfertaCargando] = useState(false);
  // Moneda real desde la oferta del proyecto. Si esta seteada, la UI bloquea
  // el selector de moneda y el form usa este valor (espejado por el backend).
  const [monedaOferta, setMonedaOferta]               = useState(null);
  // facturar_con desde el PROYECTO (heredado de oferta + override en proyecto).
  // Si esta seteado, la UI bloquea el selector y el backend lo espeja.
  const [facturarConProy, setFacturarConProy]         = useState(null);

  const cargarLineasOferta = async (numeroProyecto, populateEmpresa = false) => {
    if (!numeroProyecto) { setLineasOferta([]); setMonedaOferta(null); setFacturarConProy(null); return; }
    const proy = proyectos.find(p => p.id_proyecto === numeroProyecto);
    if (!proy?.id) { setLineasOferta([]); setMonedaOferta(null); setFacturarConProy(null); return; }
    try {
      const r = await axios.get(`${API}/cobros-cliente?action=lineas-oferta&proyecto_id=${proy.id}`);
      const lineas = r.data || [];
      setLineasOferta(lineas);
      if (lineas.length > 0) {
        if (lineas[0].moneda)       setMonedaOferta(lineas[0].moneda);
        if (lineas[0].facturar_con) setFacturarConProy(lineas[0].facturar_con);
        if (populateEmpresa) {
          setForm(f => ({
            ...f,
            moneda:       lineas[0].moneda       || f.moneda,
            facturar_con: lineas[0].facturar_con || f.facturar_con
          }));
        }
      } else {
        setMonedaOferta(null);
        setFacturarConProy(null);
      }
    } catch { setLineasOferta([]); setMonedaOferta(null); setFacturarConProy(null); }
  };

  const cargarHitosOferta = async (numeroProyecto, ivaDefecto = 13, target = null) => {
    setOcCargada(false); setOcSkip(false); setOcActiva(null);
    if (!numeroProyecto) { setLineasOferta([]); setMonedaOferta(null); setFacturarConProy(null); return; }
    const proy = proyectos.find(p => p.id_proyecto === numeroProyecto);
    if (!proy?.id) { setLineasOferta([]); setMonedaOferta(null); setFacturarConProy(null); return; }
    // v43: OC activa del proyecto (warning de fondos + captura progresiva). No bloquea si falla.
    try { const roc = await axios.get(`${API}/ordenes-compra?action=activa&proyecto_id=${proy.id}`); setOcActiva(roc.data && roc.data.activa ? roc.data.activa : null); } catch { setOcActiva(null); }
    setOcCargada(true);
    setHitosOfertaCargando(true);
    setLineasOferta([]); // limpiar datos de proyecto anterior
    setMonedaOferta(null);
    setFacturarConProy(null);
    try {
      const r = await axios.get(`${API}/cobros-cliente?action=hitos-oferta&proyecto_id=${proy.id}`);
      const hitos = r.data || [];
      if (hitos.length > 0) {
        // Extraer lineas unicas del mismo response (sin race con form.hitos)
        const lineasMap = new Map();
        hitos.forEach(h => {
          if (h.oferta_linea_id && !lineasMap.has(h.oferta_linea_id)) {
            lineasMap.set(h.oferta_linea_id, {
              id:                h.oferta_linea_id,
              numero_linea:      h.numero_linea,
              descripcion_corta: h.descripcion_corta,
              monto:             h.linea_monto,
              exonerado:         h.exonerado
            });
          }
        });
        setLineasOferta(
          Array.from(lineasMap.values()).sort((a, b) => a.numero_linea - b.numero_linea)
        );
        if (hitos[0].moneda)       setMonedaOferta(hitos[0].moneda);
        if (hitos[0].facturar_con) setFacturarConProy(hitos[0].facturar_con);
        setForm(f => ({
          ...f,
          moneda:          hitos[0].moneda       || f.moneda,
          facturar_con:    hitos[0].facturar_con || f.facturar_con,
          hitos: hitos.map((h, i) => {
            // v42: hito ya facturado/cobrado -> nunca preseleccionado (se atenua en el render).
            // Premarcado inteligente: solo el hito objetivo de la celda clicada; si no hay
            // hito objetivo (legacy hito_id NULL o apertura sin contexto), fallback a todos
            // los pendientes de la linea objetivo; sin contexto -> ninguno preseleccionado.
            const yaCobrado = !!h.ya_cobrado;
            let sel = false;
            if (!yaCobrado && target) {
              if (target.hitoId != null)             sel = (h.oferta_hito_id === target.hitoId);
              else if (target.lineaOfertaId != null) sel = (h.oferta_linea_id === target.lineaOfertaId);
            }
            return {
              numero_hito:     i + 1,
              descripcion:     h.nombre_hito || '',
              monto:           h.monto_calculado || '',
              fecha_pago:      '',
              iva_porcentaje:  h.exonerado ? 0 : ivaDefecto,
              oferta_linea_id: h.oferta_linea_id || null,
              _sel:            sel,
              _yaCobrado:      yaCobrado,
              _oferta_hito_id: h.oferta_hito_id
            };
          })
        }));
      } else {
        // Oferta sin hitos definidos - cargar lineas igual para agregar manualmente
        await cargarLineasOferta(numeroProyecto, true);
      }
    } catch (e) { console.error('Error cargando hitos oferta:', e); }
    finally { setHitosOfertaCargando(false); }
  };

  // Al abrir con proyecto preseleccionado, poblar:
  //  - edicion -> lineas de oferta (para el dropdown de linea de cada hito)
  //  - nuevo con proyecto (ej. "Solicitar factura" desde la matriz) -> hitos
  useEffect(() => {
    if (!initialForm?.numero_proyecto) return;
    if (editando) {
      cargarLineasOferta(initialForm.numero_proyecto);
    } else {
      const proy = proyectos.find(p => p.id_proyecto === initialForm.numero_proyecto);
      cargarHitosOferta(initialForm.numero_proyecto, proy?.exonerado ? 0 : 13, {
        hitoId:        initialForm.targetHitoId ?? null,
        lineaOfertaId: initialForm.targetLineaOfertaId ?? null
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── HITOS ───────────────────────────────────────────────────────────────────
  const agregarHito    = () => setForm(f => ({ ...f, hitos: [...f.hitos, { numero_hito: f.hitos.length + 1, descripcion: '', monto: '', fecha_pago: '', iva_porcentaje: 13, oferta_linea_id: null, _sel: true }] }));
  const eliminarHito   = (i) => setForm(f => ({ ...f, hitos: f.hitos.filter((_, idx) => idx !== i).map((h, idx) => ({ ...h, numero_hito: idx + 1 })) }));
  const actualizarHito = (i, k, v) => setForm(f => { const hitos = [...f.hitos]; hitos[i] = { ...hitos[i], [k]: v }; return { ...f, hitos }; });

  const cerrarModal = () => { if (window.confirm('¿Salir sin guardar? Los cambios se perderán.')) onClose(); };

  // v43: registrar el monto de OC desde el propio flujo de facturacion (captura progresiva).
  // Abierto a quien factura (NO requiere admin). Al guardar, la OC activa previa pasa a Agotada.
  const guardarOC = async () => {
    const proy = proyectos.find(p => p.id_proyecto === form.numero_proyecto);
    if (!proy?.id) { setError('No se pudo resolver el proyecto para la OC.'); return; }
    if (!ocForm.monto_sin_iva || !ocForm.fecha_emision) { setError('El monto de OC y la fecha son requeridos.'); return; }
    setOcGuardando(true); setError('');
    try {
      await axios.post(`${API}/ordenes-compra`, {
        proyecto_id:   proy.id,
        numero_oc:     ocForm.numero_oc || null,
        monto_sin_iva: parseFloat(ocForm.monto_sin_iva),
        moneda:        ocForm.moneda || form.moneda || 'Dólares',
        fecha_emision: ocForm.fecha_emision
      });
      const roc = await axios.get(`${API}/ordenes-compra?action=activa&proyecto_id=${proy.id}`);
      setOcActiva(roc.data && roc.data.activa ? roc.data.activa : null);
      setOcForm({ monto_sin_iva: '', numero_oc: '', fecha_emision: '', moneda: '' });
    } catch (e) { setError('Error guardando la OC: ' + (e.response?.data?.error || e.message)); }
    setOcGuardando(false);
  };

  const guardar = async () => {
    if (!form.cliente_id) { setError('Seleccioná un cliente.'); return; }
    const hitosActivos = form.hitos.filter(h => h._sel !== false);
    if (!hitosActivos.length) { setError('Seleccioná al menos un hito de cobro.'); return; }
    const hitosInvalidos = hitosActivos.filter(h => !h.monto || isNaN(parseFloat(h.monto)) || parseFloat(h.monto) <= 0);
    if (hitosInvalidos.length > 0) { setError('Todos los hitos seleccionados deben tener un monto válido.'); return; }
    setGuardando(true); setError('');
    try {
      const payload = { ...form, hitos: hitosActivos.map((h, i) => {
        const { _sel, _oferta_hito_id, _yaCobrado, ...rest } = h;
        // v46: persistir el enlace hito-nivel. En cobro nuevo viene como
        // _oferta_hito_id (de hitos-oferta); en edicion viene sin prefijo en
        // rest (de h.* del cobro guardado). Preferir el nuevo, caer al de
        // edicion, si no NULL (hitos manuales). NO usar `_oferta_hito_id ?? null`
        // a secas: en edicion borraria el valor real que trae rest.
        return { ...rest, numero_hito: i + 1, monto: parseFloat(h.monto), oferta_hito_id: _oferta_hito_id ?? rest.oferta_hito_id ?? null };
      }) };
      let cobro_id;
      if (editando) { await axios.put(`${API}/cobros-cliente?id=${form.id}`, payload); cobro_id = form.id; }
      else { const res = await axios.post(`${API}/cobros-cliente`, payload); cobro_id = res.data.id; }
      onSaved(cobro_id);
    } catch (e) { setError('Error al guardar: ' + (e.response?.data?.error || e.message)); }
    finally { setGuardando(false); }
  };

  const guardarYSolicitar = async () => {
    if (!form.cliente_id) { setError('Seleccioná un cliente.'); return; }
    const hitosActivos = form.hitos.filter(h => h._sel !== false);
    if (!hitosActivos.length) { setError('Seleccioná al menos un hito para solicitar factura.'); return; }
    const hitosInvalidos = hitosActivos.filter(h => !h.monto || isNaN(parseFloat(h.monto)) || parseFloat(h.monto) <= 0);
    if (hitosInvalidos.length) { setError('Todos los hitos seleccionados deben tener un monto válido.'); return; }
    setGuardando(true); setError('');
    try {
      const payload = { ...form, hitos: hitosActivos.map((h, i) => {
        const { _sel, _oferta_hito_id, _yaCobrado, ...rest } = h;
        // v46: persistir el enlace hito-nivel. En cobro nuevo viene como
        // _oferta_hito_id (de hitos-oferta); en edicion viene sin prefijo en
        // rest (de h.* del cobro guardado). Preferir el nuevo, caer al de
        // edicion, si no NULL (hitos manuales). NO usar `_oferta_hito_id ?? null`
        // a secas: en edicion borraria el valor real que trae rest.
        return { ...rest, numero_hito: i + 1, monto: parseFloat(h.monto), oferta_hito_id: _oferta_hito_id ?? rest.oferta_hito_id ?? null };
      }) };
      let cobro_id;
      if (editando) {
        await axios.put(`${API}/cobros-cliente?id=${form.id}`, payload);
        cobro_id = form.id;
      } else {
        const res = await axios.post(`${API}/cobros-cliente`, payload);
        cobro_id = res.data.id;
      }
      // Obtener IDs de los hitos recien guardados
      const resDetalle = await axios.get(`${API}/cobros-cliente?id=${cobro_id}`);
      const hitoIds = (resDetalle.data.hitos || []).map(h => h.id);
      if (!hitoIds.length) throw new Error('No se encontraron hitos para solicitar');
      // Abrir vista previa del correo. El envio (y dejar los hitos en Solicitada)
      // solo ocurre al confirmar en el preview. El cobro ya quedo guardado.
      setPreviewSolicitud({ cobroId: cobro_id, ids: hitoIds.join(',') });
    } catch (e) { setError('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setGuardando(false); }
  };

  const campo = (label, key, tipo = 'text', opciones = null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>{label}</label>
      {opciones
        ? <select value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle}>
            <option value=''>Seleccionar...</option>
            {opciones.map(o => <option key={o}>{o}</option>)}
          </select>
        : <input type={tipo} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
      }
    </div>
  );

  if (!form) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 100, overflowY: 'auto', paddingTop: '24px', paddingBottom: '24px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', width: '720px', maxWidth: '96vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#012E40', fontSize: '16px', fontWeight: '700', margin: 0 }}>
            {editando ? 'Editar cobro' : 'Nuevo cobro a cliente'}
          </h3>
          <button onClick={cerrarModal} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#aaa' }}>×</button>
        </div>

        {error && <div style={{ background: '#fdecea', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#d32f2f', fontSize: '13px' }}>⚠️ {error}</div>}

        {editando && monedaOferta && form.moneda && form.moneda !== monedaOferta && (
          <div style={{ background: '#fffaf3', border: '1px solid #f0d39c', borderLeft: '4px solid #d9a441', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#8a5a00', fontSize: '12.5px', lineHeight: '1.5' }}>
            ⚠ Este cobro tiene moneda <strong>{form.moneda}</strong> pero la oferta es <strong>{monedaOferta}</strong>. Al guardar se sincronizará a {monedaOferta}.
          </div>
        )}

        {editando && facturarConProy && form.facturar_con && form.facturar_con !== facturarConProy && (
          <div style={{ background: '#fffaf3', border: '1px solid #f0d39c', borderLeft: '4px solid #d9a441', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#8a5a00', fontSize: '12.5px', lineHeight: '1.5' }}>
            ⚠ Este cobro está marcado para facturar con <strong>{form.facturar_con}</strong> pero el proyecto manda <strong>{facturarConProy}</strong>. Al guardar se sincronizará a {facturarConProy}.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {/* Cliente */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>Cliente *</label>
            <select value={form.cliente_id || ''} onChange={e => {
              setForm({ ...form, cliente_id: e.target.value, numero_proyecto: '' });
              setLineasOferta([]);
              setMonedaOferta(null);
              setFacturarConProy(null);
            }} style={inputStyle}>
              <option value=''>Seleccionar cliente...</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          {/* Proyecto */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#666', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
              Proyecto (opcional)
              {proyectos.find(p => p.id_proyecto === form.numero_proyecto)?.exonerado &&
                <span style={{ background: '#e6f1fb', color: '#185FA5', fontSize: '10px', padding: '1px 7px', borderRadius: '8px', fontWeight: '600' }}>Exonerado — IVA 0%</span>
              }
            </label>
            <select
              value={form.numero_proyecto || ''}
              disabled={!form.cliente_id}
              onChange={e => {
                const np   = e.target.value;
                const proy = proyectos.find(p => p.id_proyecto === np);
                const iva  = proy?.exonerado ? 0 : 13;
                setForm(f => ({
                  ...f,
                  numero_proyecto: np,
                  hitos: editando
                    // Cambiar de proyecto invalida los enlaces a la oferta vieja:
                    // nulear linea Y hito (v46) para no dejar un oferta_hito_id
                    // que ya no corresponde al proyecto seleccionado.
                    ? f.hitos.map(h => ({ ...h, iva_porcentaje: iva, oferta_linea_id: null, oferta_hito_id: null, _oferta_hito_id: null }))
                    : []
                }));
                if (editando) {
                  cargarLineasOferta(np);
                } else if (np) {
                  cargarHitosOferta(np, iva);
                } else {
                  setMonedaOferta(null);
                  setFacturarConProy(null);
                }
              }}
              style={{ ...inputStyle, opacity: !form.cliente_id ? 0.5 : 1, cursor: !form.cliente_id ? 'not-allowed' : 'default' }}>
              <option value=''>{!form.cliente_id ? 'Seleccionar cliente primero' : 'Sin proyecto asociado'}</option>
              {proyectos
                .filter(p => String(p.cliente_id) === String(form.cliente_id))
                .map(p => <option key={p.id} value={p.id_proyecto}>{p.id_proyecto} — {p.nombre}{p.exonerado ? ' ★ Exonerado' : ''}</option>)}
            </select>
          </div>

          {campo('N° Factura Cliente', 'numero_factura_cliente')}
          {facturarConProy ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
                Facturar con * <span style={{ color: '#888', fontWeight: 400 }}>(desde el proyecto)</span>
              </label>
              <div style={{ ...inputStyle, background: '#f5f5f5', color: '#012E40', fontWeight: 600 }}>
                {facturarConProy}
              </div>
            </div>
          ) : campo('Facturar con *', 'facturar_con', 'text', EMPRESAS_FACT)}
          {monedaOferta ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', color: '#666', fontWeight: '500' }}>
                Moneda * <span style={{ color: '#888', fontWeight: 400 }}>(desde la oferta)</span>
              </label>
              <div style={{ ...inputStyle, background: '#f5f5f5', color: '#012E40', fontWeight: 600 }}>
                {monedaOferta}
              </div>
            </div>
          ) : campo('Moneda *', 'moneda', 'text', MONEDAS)}
          {campo('Estado', 'estado', 'text', ESTADOS_COBRO)}
          {campo('Fecha Emisión', 'fecha_emision', 'date')}
          <div style={{ gridColumn: '1/-1' }}>{campo('Descripción / Notas', 'notas')}</div>
        </div>

        {/* HITOS */}
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h4 style={{ color: '#012E40', fontSize: '14px', fontWeight: '600', margin: 0 }}>Hitos de cobro</h4>
              {hitosOfertaCargando && (
                <span style={{ fontSize: '11px', color: '#185FA5', background: '#e6f1fb', padding: '2px 10px', borderRadius: '8px' }}>Cargando hitos de oferta...</span>
              )}
              {!hitosOfertaCargando && form.hitos.length > 0 && (
                <span style={{ fontSize: '11px', color: '#3B6D11', background: '#eaf3de', padding: '2px 10px', borderRadius: '8px' }}>
                  {form.hitos.filter(h => h._sel !== false).length} de {form.hitos.length} seleccionados
                </span>
              )}
              {!hitosOfertaCargando && form.hitos.some(h => h._yaCobrado) && (
                <button onClick={() => setMostrarCobrados(v => !v)}
                  style={{ fontSize: '11px', color: '#888', background: 'transparent', border: '1px solid #ddd', borderRadius: '8px', padding: '2px 10px', cursor: 'pointer' }}>
                  {mostrarCobrados ? 'Ocultar cobrados' : `Mostrar cobrados (${form.hitos.filter(h => h._yaCobrado).length})`}
                </button>
              )}
            </div>
            <button onClick={agregarHito} style={{ padding: '5px 12px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>+ Agregar hito</button>
          </div>
          {form.numero_proyecto && ocCargada && !hitosOfertaCargando && (() => {
            if (ocActiva) {
              const totalSel = form.hitos.filter(h => h._sel !== false).reduce((s, h) => s + (parseFloat(h.monto) || 0), 0);
              const excede = totalSel > Number(ocActiva.saldo) + 0.01;
              return (
                <div style={{ background: excede ? '#FDF2E0' : '#EAF3DE', border: '1px solid ' + (excede ? '#e8c893' : '#b8dfc8'), borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: excede ? '#9A6414' : '#3B6D11', marginBottom: '10px' }}>
                  <b>OC {ocActiva.numero_oc || '(sin N°)'}</b> — saldo {fmt(Number(ocActiva.saldo), ocActiva.moneda)} (sin IVA).
                  {excede && <> ⚠ La selección ({fmt(totalSel, form.moneda)}) supera el saldo de la OC. Podés continuar de todos modos.</>}
                </div>
              );
            }
            if (ocSkip) return null;
            // v43 captura progresiva: sin OC registrada -> pedir el monto (o "Sin OC (no aplica)").
            return (
              <div style={{ background: '#eef3f7', border: '1px solid #cfe0ec', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', color: '#185FA5', fontWeight: 600, marginBottom: '8px' }}>Este proyecto no tiene monto de OC registrado. Cargalo para llevar el control de fondos, o marcá "Sin OC".</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input type="number" placeholder="Monto de OC (sin IVA)" value={ocForm.monto_sin_iva} onChange={e => setOcForm(f => ({ ...f, monto_sin_iva: e.target.value }))} style={{ ...inputStyle, padding: '6px 8px' }} />
                  <input placeholder="N° de OC (opcional)" value={ocForm.numero_oc} onChange={e => setOcForm(f => ({ ...f, numero_oc: e.target.value }))} style={{ ...inputStyle, padding: '6px 8px' }} />
                  <input type="date" value={ocForm.fecha_emision} onChange={e => setOcForm(f => ({ ...f, fecha_emision: e.target.value }))} style={{ ...inputStyle, padding: '6px 8px' }} />
                  <select value={ocForm.moneda || form.moneda || 'Dólares'} onChange={e => setOcForm(f => ({ ...f, moneda: e.target.value }))} style={{ ...inputStyle, padding: '6px 8px' }}>{['Dólares', 'Colones'].map(m => <option key={m} value={m}>{m}</option>)}</select>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                  <button onClick={() => setOcSkip(true)} style={{ padding: '6px 12px', border: '1px solid #ddd', background: '#fff', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', color: '#555' }}>Sin OC (no aplica)</button>
                  <button onClick={guardarOC} disabled={ocGuardando} style={{ padding: '6px 12px', background: '#87B749', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', opacity: ocGuardando ? 0.6 : 1 }}>{ocGuardando ? 'Guardando...' : 'Guardar OC'}</button>
                </div>
              </div>
            );
          })()}
          {form.hitos.length === 0
            ? <p style={{ color: '#aaa', fontSize: '13px', textAlign: 'center', padding: '16px' }}>
                {form.numero_proyecto ? 'Sin hitos en la oferta asociada' : 'Seleccioná un proyecto o agregá hitos manualmente'}
              </p>
            : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '8px 10px', width: '32px' }}></th>
                    {['#', 'Descripción del hito', 'Línea de oferta', 'Monto base', 'IVA %', 'Total c/IVA', ''].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: ['Monto base','Total c/IVA'].includes(h) ? 'right' : 'left', color: '#666', fontWeight: '500', fontSize: '12px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Agrupar hitos por linea de oferta (preservando orden) para dejar
                    // evidente que se pueden facturar hitos de VARIAS lineas en un solo
                    // cobro/correo. El checkbox del encabezado marca toda la linea.
                    const orden = [];
                    const porLinea = new Map();
                    form.hitos.forEach((h, i) => {
                      const key = h.oferta_linea_id != null ? String(h.oferta_linea_id) : '__sin__';
                      if (!porLinea.has(key)) { porLinea.set(key, []); orden.push(key); }
                      porLinea.get(key).push({ h, i });
                    });
                    const lineaLabel = (key) => {
                      if (key === '__sin__') return 'Hitos sin linea asignada';
                      const l = lineasOferta.find(x => String(x.id) === key);
                      return l ? `Linea ${l.numero_linea}: ${l.descripcion_corta || ''}` : `Linea (id ${key})`;
                    };
                    const toggleLinea = (items, valor) => setForm(f => {
                      // v42: el "marcar toda la linea" NO toca los hitos ya cobrados.
                      const idxs = new Set(items.filter(it => !it.h._yaCobrado).map(it => it.i));
                      return { ...f, hitos: f.hitos.map((hh, ii) => idxs.has(ii) ? { ...hh, _sel: valor } : hh) };
                    });
                    const soloUnGrupo = orden.length <= 1;
                    return orden.map(key => {
                      const items = porLinea.get(key);
                      // v42: los cobrados se ocultan salvo que el toggle este activo; si la
                      // linea queda sin filas visibles, se omite el grupo entero.
                      const visibles = items.filter(it => !it.h._yaCobrado || mostrarCobrados);
                      if (visibles.length === 0) return null;
                      const seleccionables = items.filter(it => !it.h._yaCobrado);
                      const todosSel = seleccionables.length > 0 && seleccionables.every(it => it.h._sel !== false);
                      return (
                      <React.Fragment key={key}>
                        {!soloUnGrupo && (
                          <tr style={{ background: '#eef3f7' }}>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                              <input type='checkbox' checked={todosSel} onChange={e => toggleLinea(items, e.target.checked)}
                                title='Marcar/desmarcar todos los hitos de esta linea'
                                style={{ cursor: 'pointer', width: '15px', height: '15px', accentColor: '#185FA5' }} />
                            </td>
                            <td colSpan={7} style={{ padding: '6px 10px', fontSize: '12px', fontWeight: '700', color: '#012E40' }}>{lineaLabel(key)}</td>
                          </tr>
                        )}
                        {visibles.map(({ h, i }) => {
                          const activo = h._sel !== false;
                          const cobrado = !!h._yaCobrado;
                          const premarcado = activo && !cobrado;
                          return (
                          <tr key={i} style={{
                            borderTop: '1px solid #f0f0f0',
                            opacity: cobrado ? 0.55 : (activo ? 1 : 0.5),
                            background: premarcado ? 'rgba(135,183,73,0.10)' : (cobrado ? '#fafafa' : 'transparent'),
                            borderLeft: premarcado ? '3px solid #87B749' : '3px solid transparent'
                          }}>
                      <td style={{ padding: '6px 10px', width: '32px', textAlign: 'center' }}>
                        <input type='checkbox' checked={activo} disabled={cobrado} onChange={e => actualizarHito(i, '_sel', e.target.checked)}
                          title={cobrado ? 'Ya facturado/cobrado - no re-facturable' : undefined}
                          style={{ cursor: cobrado ? 'not-allowed' : 'pointer', width: '15px', height: '15px', accentColor: '#87B749' }} />
                      </td>
                      <td style={{ padding: '6px 8px', color: '#888', width: '24px' }}>{h.numero_hito}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {premarcado && <div style={{ fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.4px', color: '#3B6D11', marginBottom: '2px' }}>PREMARCADO</div>}
                        {cobrado && <div style={{ fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.4px', color: '#999', marginBottom: '2px' }}>YA COBRADO</div>}
                        <input value={h.descripcion} onChange={e => actualizarHito(i, 'descripcion', e.target.value)}
                          disabled={!activo}
                          style={{ ...inputStyle, padding: '5px 8px', background: activo ? '#fff' : '#f9f9f9', textDecoration: cobrado ? 'line-through' : 'none', color: cobrado ? '#999' : undefined }} placeholder='Ej: Anticipo 50%' />
                      </td>
                      <td style={{ padding: '6px 4px', width: '180px' }}>
                        {!form.numero_proyecto
                          ? <select disabled style={{ ...inputStyle, padding: '5px 8px', fontSize: '11px', opacity: 0.45, cursor: 'not-allowed' }}>
                              <option>Seleccionar proyecto primero</option>
                            </select>
                          : lineasOferta.length === 0
                            ? <select disabled style={{ ...inputStyle, padding: '5px 8px', fontSize: '11px', opacity: 0.55, cursor: 'not-allowed' }}>
                                <option>Sin líneas de oferta</option>
                              </select>
                            : <select value={h.oferta_linea_id || ''} onChange={e => actualizarHito(i, 'oferta_linea_id', e.target.value || null)}
                                disabled={!activo}
                                style={{ ...inputStyle, padding: '5px 8px', fontSize: '11px', background: activo ? '#fff' : '#f9f9f9' }}>
                                <option value=''>Sin línea</option>
                                {lineasOferta.map(l => (
                                  <option key={l.id} value={l.id}>L{l.numero_linea} — {l.descripcion_corta}</option>
                                ))}
                              </select>
                        }
                      </td>
                      <td style={{ padding: '6px 4px', width: '110px' }}>
                        <input type='number' value={h.monto} onChange={e => actualizarHito(i, 'monto', e.target.value)}
                          disabled={!activo}
                          style={{ ...inputStyle, padding: '5px 8px', textAlign: 'right', background: activo ? '#fff' : '#f9f9f9' }} placeholder='0.00' />
                      </td>
                      <td style={{ padding: '6px 4px', width: '70px' }}>
                        <input type='number' value={h.iva_porcentaje ?? 13} onChange={e => actualizarHito(i, 'iva_porcentaje', e.target.value)}
                          disabled={!activo}
                          style={{ ...inputStyle, padding: '5px 8px', textAlign: 'right', background: activo ? '#fff' : '#f9f9f9' }} placeholder='13' min='0' max='100' />
                      </td>
                      <td style={{ padding: '6px 8px', width: '110px', textAlign: 'right', fontWeight: '600', color: '#012E40', fontSize: '13px' }}>
                        {activo ? fmt(montoConIva(h), form.moneda) : '—'}
                      </td>
                      <td style={{ padding: '6px 4px', width: '24px' }}>
                        <button onClick={() => eliminarHito(i)} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '16px' }}>×</button>
                      </td>
                    </tr>
                          );
                        })}
                      </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '1px solid #ddd' }}>
                    <td colSpan={4} style={{ padding: '7px 8px', color: '#888', textAlign: 'right', fontSize: '12px' }}>Subtotal seleccionados (sin IVA)</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', color: '#555', fontSize: '12px' }}>
                      {fmt(form.hitos.filter(h => h._sel !== false).reduce((s, h) => s + (parseFloat(h.monto) || 0), 0), form.moneda)}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                  <tr style={{ borderTop: '2px solid #012E40' }}>
                    <td colSpan={4} style={{ padding: '10px 8px', fontWeight: '700', color: '#012E40', textAlign: 'right' }}>Total seleccionados c/IVA</td>
                    <td></td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#012E40', fontSize: '15px' }}>
                      {fmt(form.hitos.filter(h => h._sel !== false).reduce((s, h) => s + montoConIva(h), 0), form.moneda)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
          }
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
          <button onClick={cerrarModal} disabled={guardando} style={{ padding: '8px 20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>Cancelar</button>
          <button onClick={guardar} disabled={guardando}
            style={{ padding: '8px 20px', background: guardando ? '#aaa' : '#87B749', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: guardando ? 'not-allowed' : 'pointer' }}>
            {guardando ? 'Guardando...' : (editando ? 'Guardar cambios' : 'Guardar borrador')}
          </button>
          {form.hitos.some(h => h._sel !== false) && (
            <button onClick={guardarYSolicitar} disabled={guardando}
              style={{ padding: '8px 20px', background: guardando ? '#aaa' : '#012E40', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: guardando ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {guardando ? 'Procesando...' : '📤 Guardar y revisar factura'}
            </button>
          )}
        </div>
      </div>

      {previewSolicitud && (
        <PreviewCorreo
          titulo="Solicitud de factura"
          previewUrl={`${API}/cobros-cliente?action=preview-factura&hito_ids=${previewSolicitud.ids}`}
          sendUrl={`${API}/cobros-cliente?action=solicitar-factura&hito_ids=${previewSolicitud.ids}`}
          enviarLabel="Enviar solicitud"
          onClose={() => { const cid = previewSolicitud.cobroId; setPreviewSolicitud(null); onSaved(cid); }}
          onSent={() => { const cid = previewSolicitud.cobroId; setPreviewSolicitud(null); onSaved(cid); }}
        />
      )}
    </div>
  );
}

export default ModalCobroCliente;
